/**
 * The sign-in session (`{ token, user }`, a demo or a wallet account), stored
 * under "aile.auth"; `api.ts` reads only the token. The backend's user payload
 * is untrusted shape, normalised here so `kind`/`name`/`initials` always exist.
 */

import { storageKeys } from "../config";
import { api, ApiError, type AuthUser } from "./api";
import { getSupabaseToken } from "./emailGate";
import { shortHash } from "./format";
import { getAuthDomain, getAuthUri, getChainId } from "./runtimeConfig";
import * as wallet from "./wallet";

export type SessionKind = "demo" | "wallet";

/** Normalised, always-complete profile of the signed-in user. */
export interface SessionUser {
  kind: SessionKind;
  /** Lowercase 0x address — wallet sessions only. */
  address?: string;
  /** Demo sessions: the login email. Wallet sessions: the email-gate bind. */
  email?: string;
  /** Wallet sessions: whether the bound email passed the venue allowlist. */
  emailVerified?: boolean;
  name: string;
  initials: string;
}

interface StoredSession {
  token: string;
  user: SessionUser;
}

/** "0x1234…abcd" — the sidebar/label form of an address. */
export function shortAddress(address: string): string {
  return shortHash(address, 4);
}

function initialsFor(user: AuthUser, address: string | undefined): string {
  if (typeof user.initials === "string" && user.initials.trim() !== "") {
    return user.initials.slice(0, 2).toUpperCase();
  }
  if (address !== undefined) return address.slice(2, 4).toUpperCase();
  const source = user.name ?? user.email ?? "";
  const parts = source.split(/[\s@._-]+/).filter((p) => p !== "");
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  const derived = `${first}${second}`.toUpperCase();
  return derived === "" ? "?" : derived;
}

/**
 * Folds an optional-everything backend user into a `SessionUser`;
 * `fallbackAddress` is the wallet handshake's, for when the backend omits it.
 */
export function normalizeUser(user: AuthUser, fallbackAddress?: string): SessionUser {
  const rawAddress = typeof user.address === "string" ? user.address : fallbackAddress;
  const address = rawAddress === undefined ? undefined : rawAddress.toLowerCase();
  const email = typeof user.email === "string" ? user.email : undefined;
  const kind: SessionKind =
    user.kind === "wallet" || user.kind === "demo"
      ? user.kind
      : address !== undefined
        ? "wallet"
        : "demo";
  const name =
    typeof user.name === "string" && user.name !== ""
      ? user.name
      : address !== undefined
        ? shortAddress(address)
        : (email ?? "Signed in");
  return {
    kind,
    ...(address !== undefined ? { address } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(user.emailVerified === true ? { emailVerified: true } : {}),
    name,
    initials: initialsFor(user, address),
  };
}

/**
 * A stored user is usable when it can name itself (name + initials) or carries
 * an address we can derive a name from. `kind` may be missing: sessions
 * written before wallet login existed are migrated by `normalizeUser` on read.
 */
function isStoredUser(value: unknown): value is AuthUser {
  if (value === null || typeof value !== "object") return false;
  const u = value as AuthUser;
  return (
    (typeof u.name === "string" && typeof u.initials === "string") ||
    typeof u.address === "string"
  );
}

function readSession(): StoredSession | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKeys.auth);
  } catch {
    // Storage unavailable (privacy mode) — treated as signed out; the login
    // page still works for the session lifetime of the tab.
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === "object") {
      const candidate = parsed as { token?: unknown; user?: unknown };
      if (typeof candidate.token === "string" && isStoredUser(candidate.user)) {
        return { token: candidate.token, user: normalizeUser(candidate.user) };
      }
    }
  } catch {
    // Corrupt value — fall through and self-heal by clearing it.
  }
  clearSession();
  return null;
}

function writeSession(session: StoredSession): void {
  try {
    localStorage.setItem(storageKeys.auth, JSON.stringify(session));
  } catch {
    // Storage unavailable — the user stays signed in only until reload.
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(storageKeys.auth);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/** Bearer token of the current session, or null when signed out. */
export function getStoredToken(): string | null {
  return readSession()?.token ?? null;
}

/** Profile of the signed-in user, or null when signed out. */
export function getUser(): SessionUser | null {
  return readSession()?.user ?? null;
}

/** Connected wallet address of the current session, or null. */
export function getAddress(): string | null {
  return readSession()?.user.address ?? null;
}

export function isAuthenticated(): boolean {
  return readSession() !== null;
}

/**
 * Exchange credentials for a session and persist it.
 * Throws ApiError (status 401 on bad credentials) — callers branch on status.
 */
export async function login(email: string, password: string): Promise<SessionUser> {
  const res = await api.login({ email, password });
  const user = normalizeUser(res.user ?? {});
  writeSession({ token: res.token, user });
  return user;
}

/**
 * Progress of `loginWithWallet`, surfaced so the login screen can narrate it.
 * "pairing" is WalletConnect-only: `enable()` is opening the QR sheet, which
 * covers the page once AppKit has rendered it.
 */
export type WalletLoginStage =
  | "connecting"
  | "pairing"
  | "network"
  | "nonce"
  | "signing"
  | "verifying";

/**
 * Last-resort EIP-4361 message, used only when the gateway answers the nonce
 * challenge without a ready-to-sign `message`. It will not verify against a
 * strict SIWE parser, but it keeps the flow debuggable instead of dead.
 */
function fallbackSiweMessage(address: string, nonce: string, chainId: number): string {
  const location = typeof window !== "undefined" ? window.location : undefined;
  const domain = getAuthDomain() ?? location?.host ?? "ailefx.io";
  const uri = getAuthUri() ?? location?.origin ?? "https://ailefx.io";
  return [
    `${domain} wants you to sign in with your Ethereum account:`,
    address,
    "",
    "Sign in to AileFX",
    "",
    `URI: ${uri}`,
    "Version: 1",
    `Chain ID: ${chainId}`,
    `Nonce: ${nonce}`,
    `Issued At: ${new Date().toISOString()}`,
  ].join("\n");
}

/**
 * Connect Wallet + SIWE: connect, the deployment's chain, nonce, `personal_sign`,
 * POST /auth/wallet, store the session. Call it synchronously from the click
 * (see `wallet.connect`). Throws `WalletError` (4001: rejected, never reported)
 * or `ApiError` (401: the signature did not verify).
 */
export async function loginWithWallet(
  onStage?: (stage: WalletLoginStage) => void,
  rdns?: string,
): Promise<SessionUser> {
  const chainId = getChainId();
  if (chainId === null) throw new wallet.WalletError(wallet.CHAIN_UNKNOWN, wallet.CHAIN_UNKNOWN_MESSAGE);
  onStage?.("connecting");
  // The callback is WalletConnect-only and ignored on the injected path, where
  // `connect` still reaches `eth_requestAccounts` with nothing awaited above.
  const address = await wallet.connect(rdns, () => onStage?.("pairing"));

  onStage?.("network");
  const walletChainId = await wallet.getChainId();
  if (walletChainId !== chainId) {
    await wallet.switchToChain(chainId);
  }

  onStage?.("nonce");
  const challenge = await api.authNonce(address);
  const nonce = typeof challenge.nonce === "string" ? challenge.nonce : "";
  const message =
    typeof challenge.message === "string" && challenge.message !== ""
      ? challenge.message
      : nonce !== ""
        ? fallbackSiweMessage(address, nonce, chainId)
        : null;
  if (message === null) {
    throw new ApiError(
      "Auth nonce response carried neither `message` nor `nonce`",
      "/api/v1/auth/nonce",
    );
  }

  onStage?.("signing");
  const signature = await wallet.signMessage(address, message);

  onStage?.("verifying");
  // The Supabase token lets the backend stamp the allowlist decision into the
  // session. Without one the login still proceeds: the gate is enforced at
  // intake, and /auth/me tells the UI which step to show.
  const supabaseToken = await getSupabaseToken();
  const res = await api.walletLogin({
    address,
    message,
    signature,
    ...(supabaseToken !== null ? { supabaseToken } : {}),
  });
  const user = normalizeUser(res.user ?? {}, address);
  writeSession({ token: res.token, user });
  return user;
}

/** Drop the stored session, and any WalletConnect pairing it was riding on. */
export function logout(): void {
  clearSession();
  // Synchronous: every caller navigates straight after this. The teardown is
  // fire-and-forget inside wallet.disconnect().
  wallet.disconnect();
}

/**
 * Boot-time check of the stored token against GET /api/v1/auth/me (silent —
 * this runs on every app load and must not flood the error reporter).
 * A 401 means the token is dead: the session is cleared and false returned.
 * Transient failures (network, 5xx) keep the session — a flaky gateway must
 * not log the user out.
 */
export async function validateSession(): Promise<boolean> {
  const session = readSession();
  if (session === null) return false;
  try {
    const res = await api.me({ silent: true });
    writeSession({
      token: session.token,
      user: normalizeUser(res.user ?? {}, session.user.address),
    });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      clearSession();
      // A WalletConnect pin goes too, as on every sign-out: an expired token on
      // a shared machine is when the next person sits down, and a kept pin
      // would rehydrate a pairing whose teardown did not finish.
      wallet.disconnect();
      return false;
    }
    return true;
  }
}
